/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
  formatNumber,
  getChangeRateDirection,
  getEnabledRollUps,
  formatProductAnalyticsReportDates,
  formatProductAnalyticsTableDate,
  getChartColorsSubset,
} from 'utils/charts';
// material
import CardToggles from 'material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from 'material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from 'material/detailstable/detailstable';
import BarChart from 'material/barchart/barchart';
import ChartLegend from 'material/chartlegend/chartlegend';
import ChartReportSection from 'material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './inviewembedloadsreport_styles';
import LineChart from 'material/linechart/linechart';


const AVG_IN_VIEW_TITLE = 'Embed View %'
const TOTAL_EMBED_LOADS_TITLE = 'Embed Views'
const TOTAL_PAGE_LOADS_TITLE = 'Page Loads'
/* ========== ~~~~~~~~~~ TRENDING NOW V2 ANALYTICS - INVIEW/EMBED LOADS REPORT ~~~~~~~~~~ ========== */
const InviewEmbedLoadsReport = injectSheet(styles)((props) => {
  // props
  const { selectedInterval, setSelectedInterval } = props;

  // redux state
  const { overview, details, status } = useSelector((state) => state.trendingNowV2.avgInViewReport);
  const { overview: totalEmbedLoadsOverview, details: totalEmbedLoadsDetails, status: totalEmbedLoadsStatus } = useSelector((state) => state.trendingNowV2.totalEmbedLoadsReport);

  const { overview: totalPageLoadsOverview, details: totalPageLoadsDetails, status: totalPageLoadsStatus } = useSelector((state) => state.trendingNowV2.pageViewReport);

  const [selectedReport, setSelectedReport] = useState('avg-in-view');
  const [isLoadingReport, loadReport] = useState(false);
  const timerRef = useRef(null);
  const { filters } = useSelector((state) => state.app);
  const changeType = filters.compareTo;
  const isAvgInViewSelected = selectedReport === 'avg-in-view';
  const isTotalEmbedLoadsSelected = selectedReport === 'total-embed-loads'
  const isTotalPageLoadsSelected = selectedReport === 'total-page-loads'

  // component states
  const [generatedPageViewLegends, setGeneratedPageViewLegends] = useState([]);
  const [totalEmbedLoadsChartLegends, setTotalEmbedLoadsChartLegends] = useState([]);
  const [totalPageLoadsChartLegends, setTotalPageLoadsChartLegends] = useState([]);

  const [showAvgInView, setShowAvgInView] = useState(false);
  const [enabledRollups, setEnabledRollups] = useState([]);
  const [selectedPeriodX, setSelectedPeriodX] = useState(null);

  // component constants
  const chartTableColors = getChartColorsSubset(2);
  const avgInViewProducts = [{ id: 'totalInViewRate', name: AVG_IN_VIEW_TITLE, color: chartTableColors[0] }];
  const totalEmbedLoadsProduct = [{ id: 'totalEmbedLoads', name: TOTAL_EMBED_LOADS_TITLE, color: chartTableColors[0] }];
  const totalPageLoadsProduct = [{ id: 'totalPageViews', name: TOTAL_PAGE_LOADS_TITLE, color: chartTableColors[0] }];

  const totalDatesForInterval = details ? details[selectedInterval] : {};

  // when page gets loaded, then show total avgInView and totalEmbedLoad
  useEffect(() => {
    if (status === 'fulfilled' && overview) {
      showAvgInViews();
    }
  }, [overview]);

  useEffect(() => {
    if (totalEmbedLoadsStatus === 'fulfilled' && totalEmbedLoadsOverview) {
      showTotalEmbedLoads();
    }
  }, [totalEmbedLoadsOverview]);
  
  useEffect(() => {
    if (totalPageLoadsStatus === 'fulfilled' && totalPageLoadsOverview) {
      showTotalPageLoads();
    }
  }, [totalPageLoadsOverview]);

  // when mouse hover leaves the chart show back the net total aggregate
  useEffect(() => {
    if (showAvgInView) {
      if (isAvgInViewSelected) showAvgInViews();
      else {
        if(isTotalEmbedLoadsSelected) showTotalEmbedLoads()
        else showTotalPageLoads()
      }
    }
  }, [showAvgInView]);

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => clearTimeout(timerRef.current);
  }, []);

  // disable/enable intervals based on the number of dates
  useEffect(() => {
    if (status === 'fulfilled' && enabledRollups.length === 0) {
      const rollups = getEnabledRollUps(details, 'trending');
      setEnabledRollups(rollups);
    } else {
      setEnabledRollups([]);
    }
  }, [status]);

  /**
   * show aggregate total for total avgInView
   */
  const showAvgInViews = () => {
    const productArr = avgInViewProducts.map((product) => ({
      ...product,
      value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
      changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
      checked:
        generatedPageViewLegends.length > 0
          ? generatedPageViewLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setGeneratedPageViewLegends(productArr);
  };

  /**
   * show aggregate total for total embedLoad
   */
  const showTotalEmbedLoads = () => {
    const productArr = totalEmbedLoadsProduct.map((product) => ({
      ...product,
      value: Object.keys(totalEmbedLoadsOverview).length > 0 ? totalEmbedLoadsOverview.period[product.id] : 0,
      changeRate: Object.keys(totalEmbedLoadsOverview).length > 0 ? totalEmbedLoadsOverview.period[`${product.id}RateChange`] : 0,
      checked:
        totalEmbedLoadsChartLegends.length > 0
          ? totalEmbedLoadsChartLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setTotalEmbedLoadsChartLegends(productArr);
  };

  const getBarChartKeys = () =>
    isAvgInViewSelected
      ? avgInViewProducts.map((product) => product.name).reverse()
      : totalEmbedLoadsProduct.map((product) => product.name).reverse();

  const getBarChartColors = () =>
    isAvgInViewSelected
      ? avgInViewProducts.map((product) => product.color).reverse()
      : totalEmbedLoadsProduct.map((product) => product.color).reverse();

    /**
   * show aggregate total for total page loads
   */
    const showTotalPageLoads = () => {
      const productArr = totalPageLoadsProduct.map((product) => ({
        ...product,
        value: Object.keys(totalPageLoadsOverview).length > 0 ? totalPageLoadsOverview.period[product.id] : 0,
        changeRate: Object.keys(totalPageLoadsOverview).length > 0 ? totalPageLoadsOverview.period[`${product.id}RateChange`] : 0,
        checked:
          totalPageLoadsChartLegends.length > 0
            ? totalPageLoadsChartLegends.filter((legend) => legend.id === product.id)[0].checked
            : true,
      }));
      setTotalPageLoadsChartLegends(productArr);
    };

  const renderAvgInViewLineChartLegends = () => {
    return generatedPageViewLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={`${legend.name}`}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...generatedPageViewLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setGeneratedPageViewLegends(legends);
          }}
          value={formatNumber(legend.value, 'percent')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trending'}
        />
      );
    });
  };

  const renderTotalEmbedLoadsBarChartLegends = () => {
    return totalEmbedLoadsChartLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={legend.name}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...totalEmbedLoadsChartLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setTotalEmbedLoadsChartLegends(legends);
          }}
          value={formatNumber(legend.value, 'number')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trending'}
        />
      );
    });
  };

  const renderTotalPageLoadsBarChartLegends = () => {
    return totalPageLoadsChartLegends.map((legend, index) => {
      return (
        <ChartLegend
          title={legend.name}
          checkboxColor={legend.color}
          checked={legend.checked}
          onCheck={(checked) => {
            const legends = [...totalPageLoadsChartLegends];
            legends[index] = {
              ...legends[index],
              checked: checked,
            };
            setTotalPageLoadsChartLegends(legends);
          }}
          value={formatNumber(legend.value, 'number')}
          changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'trending'}
        />
      );
    });
  };

  const transformTotalEmbedLoadBarChartData = () => {
    let barChartData = [];
    barChartData = Object.keys(totalDatesForInterval).map((date, index) => {
      const dateLength = Object.keys(totalDatesForInterval).length;
      const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
      let item = { date: formattedDate };
      totalEmbedLoadsProduct.forEach((product) => {
        if(!totalEmbedLoadsDetails || Object.keys(totalEmbedLoadsDetails[selectedInterval]).length === 0) return
        const detailsDateData = totalEmbedLoadsDetails[selectedInterval][date];
        const checkedProducts = totalEmbedLoadsChartLegends.filter((product) => product.checked === true);
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          item[product.name] = detailsDateData[product.id];
          item[`${product.name}Color`] = product.color;
          item['origDate'] = date;
          item['formattedTableDate'] = formatProductAnalyticsTableDate(
            date,
            selectedInterval,
            detailsDateData.startDate,
            detailsDateData.endDate,
          );
        }
      });
      return item;
    });
    return barChartData;
  };

  const transformAvgInViewLineChartData = () => {
    const lineChartData = [];
    const dateLength = Object.keys(totalDatesForInterval).length;
    if (details && details[selectedInterval]) {
      const checkedProducts = generatedPageViewLegends.filter((product) => product.checked === true);
      const items = [];
      avgInViewProducts.forEach((product) => {
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          Object.keys(details[selectedInterval]).forEach((date, index) => {
            const value = details[selectedInterval][date].totalInViewRate;
            // To allow duplicate x-axis labels
            let spaces = '';
            for (let i = 0; i < index; i++) spaces += ' ';
            const detailsDateData = details[selectedInterval][date];
            items.push({
              x: formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true).concat(spaces),
              y: value,
              origDate: date,
              formattedDate: formatProductAnalyticsTableDate(
                date,
                selectedInterval,
                detailsDateData.startDate,
                detailsDateData.endDate,
              ),
              productName: AVG_IN_VIEW_TITLE,
            });
          });

          lineChartData.push({
            id: product.id,
            color: product.color,
            data: items,
          });
        }
      });
    }
    return lineChartData;
  };

  const transformTotalPageLoadBarChartData = () => {
    let barChartData = [];
    barChartData = Object.keys(totalDatesForInterval).map((date, index) => {
      const dateLength = Object.keys(totalDatesForInterval).length;
      const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
      let item = { date: formattedDate };
      totalPageLoadsProduct.forEach((product) => {
        if(!totalPageLoadsDetails || Object.keys(totalPageLoadsDetails[selectedInterval]).length === 0) return
        const detailsDateData = totalPageLoadsDetails[selectedInterval][date];
        const checkedProducts = totalPageLoadsChartLegends.filter((product) => product.checked === true);
        if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
          item[product.name] = detailsDateData[product.id];
          item[`${product.name}Color`] = product.color;
          item['origDate'] = date;
          item['formattedTableDate'] = formatProductAnalyticsTableDate(
            date,
            selectedInterval,
            detailsDateData.startDate,
            detailsDateData.endDate,
          );
        }
      });
      return item;
    });
    return barChartData;
  };

  const handleHoverOnPageLoadBarChart = (barData) => {
    const date = barData.data ? barData.data.origDate : barData.origDate;
    const legends = [...totalPageLoadsChartLegends];

    totalPageLoadsProduct.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = totalPageLoadsDetails[selectedInterval][date] ? totalPageLoadsDetails[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = totalPageLoadsChartLegends.map((legend) => legend.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setTotalPageLoadsChartLegends(legends);
    setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
    setShowAvgInView(false);
  };

  const handleHoverOnEmbedLoadBarChart = (barData) => {
    const date = barData.data ? barData.data.origDate : barData.origDate;
    const legends = [...totalEmbedLoadsChartLegends];

    totalEmbedLoadsProduct.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = totalEmbedLoadsDetails[selectedInterval][date] ? totalEmbedLoadsDetails[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = totalEmbedLoadsChartLegends.map((legend) => legend.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setTotalEmbedLoadsChartLegends(legends);
    setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
    setShowAvgInView(false);
  };

  const handleHoverOnAvgInViewLineChart = (lineChartData) => {
    const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate;
    const legends = [...generatedPageViewLegends];
    avgInViewProducts.forEach((product) => {
      let value = 0;
      let changeRate = 0;
      const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
      if (productDetails) {
        value = productDetails[product.id] ? productDetails[product.id] : 0;
        changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
      }

      const legendIndex = generatedPageViewLegends.map((product) => product.id).indexOf(product.id);
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate,
      };
    });
    setGeneratedPageViewLegends(legends);
    setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`);
    setShowAvgInView(false);
  };

  const handleMouseLeave = () => {
    setShowAvgInView(true);
    setSelectedPeriodX('');
  };

  const totalEmbedLoadsBarChart = {
    indexBy: 'date',
    data: isAvgInViewSelected ? transformAvgInViewLineChartData() : transformTotalEmbedLoadBarChartData(),
    keys: getBarChartKeys(),
    colors: getBarChartColors(),
    legends: isAvgInViewSelected ? renderAvgInViewLineChartLegends() : renderTotalEmbedLoadsBarChartLegends(),
  };

  const handleSelectReport = (report) => {
    loadReport(true);
    setSelectedReport(report);
    timerRef.current = setTimeout(() => loadReport(false), 1500);
  };

  const cardtoggles = (
    <CardToggles>
      <CardToggle
        label={AVG_IN_VIEW_TITLE}
        compareType={changeType}
        metricType={'trending'}
        percentChange={{
          value: overview?.period ? formatNumber(overview.period.totalInViewRateChangeRate, 'percent') : 0,
          direction: overview?.period ? getChangeRateDirection(overview.period.totalInViewRateChangeRate) : 'up',
        }}
        selected={selectedReport === 'avg-in-view'}
        handleClick={() => handleSelectReport('avg-in-view')}
        value={overview?.period ? formatNumber(overview.period.totalInViewRate, 'percent') : ''}
      />
      <CardToggle
        label={'Embed Views'}
        compareType={changeType}
        metricType={'trending'}
        percentChange={{
          value: totalEmbedLoadsOverview?.period ? formatNumber(totalEmbedLoadsOverview.period.totalEmbedLoadsRateChange, 'percent') : 0,
          direction: totalEmbedLoadsOverview?.period ? getChangeRateDirection(totalEmbedLoadsOverview.period.totalEmbedLoadsRateChange) : 'up',
        }}
        selected={selectedReport === 'total-embed-loads'}
        handleClick={() => handleSelectReport('total-embed-loads')}
        value={totalEmbedLoadsOverview?.period ? formatNumber(totalEmbedLoadsOverview.period.totalEmbedLoads) : ''}
      />
      <CardToggle
        label={'Page Loads'}
        compareType={changeType}
        metricType={'trending'}
        percentChange={{
          value: totalPageLoadsOverview?.period ? formatNumber(totalPageLoadsOverview.period.totalPageViewsRateChange, 'percent') : 0,
          direction: totalPageLoadsOverview?.period ? getChangeRateDirection(totalPageLoadsOverview.period.totalPageViewsRateChange) : 'up',
        }}
        selected={selectedReport === 'total-page-loads'}
        handleClick={() => handleSelectReport('total-page-loads')}
        value={totalPageLoadsOverview?.period ? formatNumber(totalPageLoadsOverview.period.totalPageViews) : ''}
      />
    </CardToggles>
  );

  const formattedData = transformAvgInViewLineChartData();
  const hasSingleData = () => {
    let singleData = false;
    formattedData.forEach((item) => {
      if (item.data.length === 1) singleData = true;
    });
    return singleData;
  };

  //----------------------------------------main functions for line chart and barchart-----------------------------------------
  const barchart = (
    <BarChart
      indexBy={'date'}
      colors={totalEmbedLoadsBarChart.colors}
      keys={totalEmbedLoadsBarChart.keys}
      data={totalEmbedLoadsBarChart.data}
      period={selectedInterval}
      loading={status === 'pending' || isLoadingReport ? true : false}
      onHover={(data) => handleHoverOnEmbedLoadBarChart(data)}
      onMouseLeave={handleMouseLeave}
      reset={showAvgInView}
      totalKey={isTotalEmbedLoadsSelected ? TOTAL_EMBED_LOADS_TITLE : TOTAL_PAGE_LOADS_TITLE}
    />
  );

  const totalPageLoads = {
    indexBy: 'date',
    data: transformTotalPageLoadBarChartData(),
    keys: totalPageLoadsProduct.map((product) => product.name).reverse(),
    colors: totalPageLoadsProduct.map((product) => product.color).reverse(),
    legends: renderTotalPageLoadsBarChartLegends(),
  };

  
  const totalPageLoadsBarChart = (
    <BarChart
      indexBy={'date'}
      colors={totalPageLoads.colors}
      keys={totalPageLoads.keys}
      data={totalPageLoads.data}
      period={selectedInterval}
      loading={totalPageLoadsStatus === 'pending' || isLoadingReport ? true : false}
      onHover={(data) => handleHoverOnPageLoadBarChart(data)}
      onMouseLeave={handleMouseLeave}
      reset={showAvgInView}
    />
  );

  const lineChart = (
    <LineChart
      category={'product'}
      enablePoints={hasSingleData()}
      data={formattedData}
      onHover={handleHoverOnAvgInViewLineChart}
      onMouseLeave={handleMouseLeave}
      reset={showAvgInView}
      yType={'avg-in-view'}
      useDashedLines={true}
      period={selectedInterval}
      isPercentageData={true}
      loading={status === 'pending' || isLoadingReport ? true : false}
    />
  );

  const detailsTableData = isAvgInViewSelected ? 
  (details ? details[selectedInterval] : null) : 
    isTotalEmbedLoadsSelected ? 
      (totalEmbedLoadsDetails ? totalEmbedLoadsDetails[selectedInterval] : null) 
      : 
      (totalPageLoadsDetails ? totalPageLoadsDetails[selectedInterval] : null) 

  const detailtable = (
    <DetailsTable
      period={selectedInterval}
      tableType={'trending'}
      products={isAvgInViewSelected ? avgInViewProducts : (isTotalEmbedLoadsSelected ? totalEmbedLoadsProduct : totalPageLoadsProduct)}
      data={detailsTableData}
      csvFileName={isAvgInViewSelected ? AVG_IN_VIEW_TITLE : isTotalEmbedLoadsSelected ? TOTAL_EMBED_LOADS_TITLE : TOTAL_PAGE_LOADS_TITLE}
      dataType={isAvgInViewSelected ? 'percent' : 'number'}
    />
  );

  /**
   *
   * @returns if data is there or not to display on trending page
   */
  const isNoData = () => {
    return (
      status === 'rejected' ||
      (status === 'fulfilled' && !overview?.period?.totalInViewRate) ||
      (status === 'fulfilled' && Object.keys(overview).length === 0) ||
      (status === 'fulfilled' && Object.keys(totalPageLoadsDetails).length === 0)
    );
  };

  const tooltip = useMemo(() => {
    if(isAvgInViewSelected) 
      return {
        title: AVG_IN_VIEW_TITLE,
        content: `Displays the percentage of times the Trending Now unit was in view relative to the total number of page loads. A page load does not guarantee the unit was visible unless it was scrolled into view.`
      }
    if(isTotalEmbedLoadsSelected) 
      return {
        title: TOTAL_EMBED_LOADS_TITLE,
        content: `Indicates the total number of times the Trending Now unit was scrolled into view and seen by users.`
      }
    if(isTotalPageLoadsSelected)
      return {
        title: TOTAL_PAGE_LOADS_TITLE,
        content: `Shows the total number of page loads for articles containing the Trending Now unit.`
      }
  }, [isAvgInViewSelected, isTotalEmbedLoadsSelected, isTotalPageLoadsSelected])


  return (
    <div name={'pageviewreporttrendingnowv2'}>
      <ChartReportSection
        legendTitle={selectedPeriodX}
        title={`${AVG_IN_VIEW_TITLE}/${TOTAL_EMBED_LOADS_TITLE}`}
        tooltipContent={
          <div className={'tip-content'}>
            <div className={'tip-title'}>
              <h4>{tooltip.title}</h4>
            </div>
            <div className={'tip-desc'}>
              <p>{tooltip.content}</p>
            </div>
          </div>
        }
        cardtoggles={cardtoggles}
        chart={isAvgInViewSelected ? lineChart : isTotalEmbedLoadsSelected ? barchart : totalPageLoadsBarChart}
        note={''}
        // loading={status === 'pending' || totalPageLoadsStatus === 'pending' ? true : false}
        noData={isNoData()}
        enabledPeriods={enabledRollups}
        chartLegend={isTotalPageLoadsSelected ? renderTotalPageLoadsBarChartLegends() : totalEmbedLoadsBarChart.legends }
        handleSelectPeriod={(period) => {
          setSelectedInterval(period);
          handleMouseLeave();
        }}
        selectedPeriod={selectedInterval}
        detailTable={detailtable}
      />
    </div>
  );
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default InviewEmbedLoadsReport;
